import { MultiSelect, Select, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useEffect, useState } from 'react';
import type { FilterType } from '../../types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const DATE_PICKER_OPERATORS = new Set(['bf', 'af']);
const NO_VALUE_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

type ListOption = { label: string; value: string };

export type FilterValueFieldProps = {
  filterType: FilterType | undefined;
  operator: string;
  value: string;
  getListOptions?: () => Promise<ListOption[]>;
  onChange: (value: string) => void;
};

function NoValueFilterInput() {
  return <TextInput value="" size="sm" disabled aria-label="Filter value" />;
}

type MultiselectFilterInputProps = {
  value: string;
  listOptions: ListOption[];
  loadingOptions: boolean;
  onChange: (value: string) => void;
};

function MultiselectFilterInput({
  value,
  listOptions,
  loadingOptions,
  onChange,
}: Readonly<MultiselectFilterInputProps>) {
  const selected = value ? value.split(',').filter(Boolean) : [];
  return (
    <MultiSelect
      data={listOptions}
      value={selected}
      onChange={(vals) => onChange(vals.join(','))}
      size="sm"
      placeholder={loadingOptions ? 'Loading...' : 'Select values'}
      disabled={loadingOptions}
      aria-label="Filter value"
    />
  );
}

type SingleSelectFilterInputProps = {
  value: string;
  listOptions: ListOption[];
  loadingOptions: boolean;
  onChange: (value: string) => void;
};

function SingleSelectFilterInput({
  value,
  listOptions,
  loadingOptions,
  onChange,
}: Readonly<SingleSelectFilterInputProps>) {
  return (
    <Select
      data={listOptions}
      value={value || null}
      onChange={(val) => onChange(val ?? '')}
      size="sm"
      placeholder={loadingOptions ? 'Loading...' : 'Select a value'}
      disabled={loadingOptions}
      clearable
      aria-label="Filter value"
    />
  );
}

type DateFilterValueInputProps = {
  operator: string;
  value: string;
  onChange: (value: string) => void;
};

function DateFilterValueInput({
  operator,
  value,
  onChange,
}: Readonly<DateFilterValueInputProps>) {
  if (DATE_PICKER_OPERATORS.has(operator)) {
    return (
      <DateInput
        value={value ? dayjs(value, 'DD-MM-YYYY').toDate() : null}
        onChange={(date) =>
          onChange(date ? dayjs(date).format('DD-MM-YYYY') : '')
        }
        size="sm"
        valueFormat="DD-MM-YYYY"
        placeholder="Pick a date"
        aria-label="Filter value"
      />
    );
  }

  return (
    <TextInput
      type="number"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      size="sm"
      placeholder="Number of days (e.g. 5)"
      aria-label="Filter value"
    />
  );
}

type NumberFilterInputProps = {
  value: string;
  onChange: (value: string) => void;
};

function NumberFilterInput({
  value,
  onChange,
}: Readonly<NumberFilterInputProps>) {
  return (
    <TextInput
      type="number"
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      size="sm"
      placeholder="Enter number"
      aria-label="Filter value"
    />
  );
}

type DefaultTextFilterInputProps = {
  value: string;
  onChange: (value: string) => void;
};

function DefaultTextFilterInput({
  value,
  onChange,
}: Readonly<DefaultTextFilterInputProps>) {
  return (
    <TextInput
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      size="sm"
      placeholder="Enter value"
      aria-label="Filter value"
    />
  );
}

const FilterValueField = ({
  filterType,
  operator,
  value,
  getListOptions,
  onChange,
}: FilterValueFieldProps) => {
  const [listOptions, setListOptions] = useState<ListOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const needsOptions =
    filterType === 'select' ||
    filterType === 'multiselect' ||
    filterType === 'boolean';

  useEffect(() => {
    if (!getListOptions || !needsOptions) return;
    setLoadingOptions(true);
    getListOptions()
      .then(setListOptions)
      .catch(() => setListOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [getListOptions, needsOptions]);

  if (NO_VALUE_OPERATORS.has(operator)) {
    return <NoValueFilterInput />;
  }

  switch (filterType) {
    case 'multiselect':
      return (
        <MultiselectFilterInput
          value={value}
          listOptions={listOptions}
          loadingOptions={loadingOptions}
          onChange={onChange}
        />
      );
    case 'select':
    case 'boolean':
      return (
        <SingleSelectFilterInput
          value={value}
          listOptions={listOptions}
          loadingOptions={loadingOptions}
          onChange={onChange}
        />
      );
    case 'date':
      return (
        <DateFilterValueInput
          operator={operator}
          value={value}
          onChange={onChange}
        />
      );
    case 'number':
      return <NumberFilterInput value={value} onChange={onChange} />;
    default:
      return <DefaultTextFilterInput value={value} onChange={onChange} />;
  }
};

export default FilterValueField;
